import axios from "axios";
import { browserTimezone } from "./format.js";

/*
 * Axios instance and interceptors.
 * Ported from frontend/app/services/api.js — same headers, same 401 behaviour.
 *
 * Only two things changed:
 *   · process.env.NEXT_PUBLIC_API_URL → import.meta.env.VITE_API_URL
 *   · the 401 handler calls a registered callback instead of assigning
 *     window.location.href, so React Router navigates and the SPA is not
 *     reloaded from scratch (see registerUnauthorizedHandler below)
 */

export const API_URL = import.meta.env.VITE_API_URL || "http://localhost:5001";

/* --------------------------------------------------------------------------
   Session storage
   --------------------------------------------------------------------------
   The key and shape are deliberately IDENTICAL to the Next.js app's:
   localStorage["session"] = { user, accessToken }.
   Anyone already signed in stays signed in when the frontends are swapped.
   -------------------------------------------------------------------------- */

const SESSION_KEY = "session";

export function readSession() {
    try {
        const raw = localStorage.getItem(SESSION_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return parsed?.accessToken ? parsed : null;
    } catch {
        // Corrupt JSON — treat as signed out rather than crashing on boot.
        return null;
    }
}

export function writeSession(session) {
    try {
        localStorage.setItem(SESSION_KEY, JSON.stringify(session));
    } catch {
        /* private mode — the in-memory session still works for this tab */
    }
}

export function clearSession() {
    try {
        localStorage.removeItem(SESSION_KEY);
    } catch {
        /* nothing to do */
    }
}

/* --------------------------------------------------------------------------
   401 handling
   --------------------------------------------------------------------------
   The interceptor lives outside React, so AuthContext registers a handler that
   clears state and routes to /login. Falling back to a location assignment keeps
   the behaviour correct if a request somehow beats the provider's mount.
   -------------------------------------------------------------------------- */

let onUnauthorized = null;

export function registerUnauthorizedHandler(handler) {
    onUnauthorized = handler;
    return () => {
        if (onUnauthorized === handler) onUnauthorized = null;
    };
}

/* --------------------------------------------------------------------------
   Client
   -------------------------------------------------------------------------- */

export const api = axios.create({
    baseURL: API_URL,
    headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
    const session = readSession();
    if (session?.accessToken) {
        config.headers.Authorization = `Bearer ${session.accessToken}`;
    }

    // The backend reads this to resolve date-range queries in the user's own
    // timezone. backend/app.js already lists it in the CORS allowedHeaders — any
    // NEW custom header has to be added there too or the preflight fails.
    const tz = browserTimezone();
    if (tz) config.headers["x-user-timezone"] = tz;

    // File uploads (FormData): drop the JSON default Content-Type so axios sets
    // multipart/form-data WITH the boundary the server needs. Otherwise the
    // request goes out as application/json, multer parses no file, and the
    // backend answers "A build file is required" (App Versions upload).
    if (typeof FormData !== "undefined" && config.data instanceof FormData) {
        if (typeof config.headers?.delete === "function") config.headers.delete("Content-Type");
        else delete config.headers["Content-Type"];
    }

    return config;
});

api.interceptors.response.use(
    (response) => response,
    (error) => {
        const status = error.response?.status;
        const url = error.config?.url ?? "";

        if (status === 401) {
            // A wrong *current* password also returns 401. Signing the user out
            // mid-password-change would be both confusing and wrong, so this call
            // is exempt — same exception the Next.js app makes.
            const isPasswordChange = url.includes("/change-password");
            // Failing to sign in is not a session expiry either; the login form
            // shows its own message.
            const isLogin = url.includes("/auth/login");

            if (!isPasswordChange && !isLogin) {
                clearSession();
                if (onUnauthorized) {
                    onUnauthorized();
                } else if (window.location.pathname !== "/login") {
                    window.location.href = "/login?error=session-expired";
                }
            }
        }

        return Promise.reject(error);
    },
);

/* --------------------------------------------------------------------------
   Error message helper
   --------------------------------------------------------------------------
   The backend answers with { status, message }. Pull that out once so pages do
   not each invent their own fallback chain.
   -------------------------------------------------------------------------- */
export function apiErrorMessage(error, fallback = "Something went wrong. Please try again.") {
    if (error?.response?.data?.message) return error.response.data.message;

    // No response at all means the request never reached the API.
    if (error?.request && !error?.response) {
        return "Cannot reach the server. Check your connection and try again.";
    }

    return error?.message || fallback;
}
